// useDeviceListLiveData – live status and command maps, websocket subscriptions, and "now" tick.
// Device status IPC messages are coalesced via setTimeout(0) to limit setState churn. Do not wrap
// in startTransition — urgent UI (filters/sorts) can starve transitions and stall lastStatusSeen vs `now`,
// which makes devices look offline (online cutoff uses both).

import { useEffect, useMemo, useRef, useState } from 'react';
import type { DeviceStatus } from '../../../../types/DeviceListTypes';
import type { CommandDdbItem } from '../../../../types/Commands';
import type { CommandUpdateMessage } from '../../../../types/WebsocketSchema';
import { logConsoleError } from '../../../../shared/errorLogging';
import { useAuth } from '../../../context/AuthContext';
import {
  commandTargetRearDeviceId,
  isFrontDeviceId,
  normalizeFleetStatusDeviceId,
  pairedFrontIdFromRear,
} from '../../../helpers/devicePairing';
import { normalizeDeviceStatus, toStatusSeenMs } from './deviceListHelpers';
import { bustCache, withCache } from '../../../helpers/apiCache';

const LIVE_TTL = 30_000;

import type { UseDeviceListLiveDataOptions, DeviceListLiveDataResult } from '../types/DeviceListViewTypes';

export type { UseDeviceListLiveDataOptions, DeviceListLiveDataResult };

function normalizeDeviceIds(deviceIds?: string[] | null): string[] {
  if (!Array.isArray(deviceIds)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of deviceIds) {
    const deviceId = String(raw ?? '').trim();
    if (!deviceId || seen.has(deviceId)) continue;
    seen.add(deviceId);
    out.push(deviceId);
  }
  return out;
}

function areStringArraysEqual(a?: string[] | null, b?: string[] | null): boolean {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function deriveCommandTargetDeviceIds(cmd: Partial<CommandDdbItem>): string[] {
  const deviceId = typeof cmd.deviceId === 'string' ? cmd.deviceId.trim() : '';
  if (!deviceId) return [];

  const normalizedDeviceId = normalizeFleetStatusDeviceId(deviceId);
  const type = String(cmd.type ?? '').trim().toUpperCase();
  let isFrontTarget = type.startsWith('FRONT_');

  const rawStatusInfo = typeof cmd.status_info === 'string' ? cmd.status_info.trim() : '';
  if (!isFrontTarget && rawStatusInfo) {
    try {
      const parsed = JSON.parse(rawStatusInfo) as { ack?: { target?: unknown } };
      const ackTarget = String(parsed?.ack?.target ?? '').trim().toLowerCase();
      if (ackTarget === 'front') isFrontTarget = true;
    } catch {
      // Ignore malformed status_info payloads.
    }
  }

  if (isFrontTarget) {
    const frontId = isFrontDeviceId(normalizedDeviceId)
      ? normalizedDeviceId
      : pairedFrontIdFromRear(normalizedDeviceId);
    return frontId ? [frontId] : [normalizedDeviceId];
  }

  return [commandTargetRearDeviceId(normalizedDeviceId)];
}

export function useDeviceListLiveData(
  options?: UseDeviceListLiveDataOptions | null,
): DeviceListLiveDataResult {
  const { isAdmin } = useAuth();
  const seedDeviceIdsRaw = useMemo(() => normalizeDeviceIds(options?.deviceIds), [options?.deviceIds]);
  const stableSeedDeviceIdsRef = useRef<string[]>([]);
  if (!areStringArraysEqual(stableSeedDeviceIdsRef.current, seedDeviceIdsRaw)) {
    stableSeedDeviceIdsRef.current = seedDeviceIdsRaw;
  }
  const seedDeviceIds = stableSeedDeviceIdsRef.current;
  const scopedEventId = useMemo(
    () => String(options?.operatorScopedEventId ?? '').trim(),
    [options?.operatorScopedEventId],
  );
  const enableInitialStatusQuery = options?.enableInitialStatusQuery !== false;
  const enableInitialCommandQuery = options?.enableInitialCommandQuery !== false;
  const statusReloadKey = options?.statusReloadKey ?? '';
  const [wsReconnectKey, setWsReconnectKey] = useState(0);
  // Safety-net re-seed: even if a dropped socket is never detected/reconnected,
  // periodically re-pull status/commands from the backend so the list can't get
  // stranded "all offline" until a re-login.
  const [reseedKey, setReseedKey] = useState(0);
  const [statusMap, setStatusMap] = useState<Record<string, DeviceStatus>>({});
  const [lastStatusSeen, setLastStatusSeen] = useState<Record<string, number>>({});
  const [now, setNow] = useState(() => Date.now());
  const [latestCommandMap, setLatestCommandMap] = useState<Record<string, CommandDdbItem>>({});
  const [commandStatusMap, setCommandStatusMap] = useState<Record<string, string | undefined>>({});
  const [statusInitiallyLoaded, setStatusInitiallyLoaded] = useState(false);
  const [commandInitiallyLoaded, setCommandInitiallyLoaded] = useState(false);

  const statusPendingRef = useRef<Record<string, DeviceStatus>>({});
  const statusFlushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const optionsRef = useRef(options);
  optionsRef.current = options;

  /** Hard refresh: show loading again while status/commands are re-fetched. */
  useEffect(() => {
    if (!statusReloadKey) return;
    setStatusInitiallyLoaded(false);
    setCommandInitiallyLoaded(false);
  }, [statusReloadKey]);

  /** Subscribe to command updates (websocket); merge into latestCommandMap and request map when request is active. */
  useEffect(() => {
    if (!window.fleetAPI?.onCommandUpdate) return;

    const unsubscribe = window.fleetAPI.onCommandUpdate((msg: CommandUpdateMessage) => {
      const container = msg as unknown as { command?: unknown };
      const cmdUnknown: unknown = container.command ?? msg;

      if (!cmdUnknown || typeof cmdUnknown !== 'object') return;
      const cmd = cmdUnknown as Partial<CommandDdbItem>;

      const deviceId = typeof cmd.deviceId === 'string' ? cmd.deviceId.trim() : '';
      if (!deviceId) return;
      const targetDeviceIds = deriveCommandTargetDeviceIds(cmd);
      if (!targetDeviceIds.length) return;

      const displayStatus = typeof cmd.status === 'string' ? cmd.status.toUpperCase() : undefined;

      if (displayStatus) {
        setCommandStatusMap((prev) => {
          const next = { ...prev };
          for (const targetId of targetDeviceIds) next[targetId] = displayStatus;
          return next;
        });
      }

      setLatestCommandMap((prev) => {
        const next = { ...prev };
        const nextUpdated = typeof cmd.updatedAt === 'number' ? cmd.updatedAt : 0;

        for (const targetId of targetDeviceIds) {
          const existing = next[targetId];
          const existingUpdated = existing && typeof existing.updatedAt === 'number' ? existing.updatedAt : 0;
          if (existing && nextUpdated && nextUpdated < existingUpdated) continue;

          const nextStatus = displayStatus ?? existing?.status;
          const nextStatusInfo = typeof cmd.status_info === 'string' ? cmd.status_info : existing?.status_info;

          next[targetId] = {
            ...(existing || {}),
            ...(cmd as CommandDdbItem),
            deviceId: targetId,
            status: nextStatus,
            status_info: nextStatusInfo,
          } as CommandDdbItem;
        }

        return next;
      });

      const opts = optionsRef.current;
      if (opts?.activeRequestIdRef?.current && opts?.setRequestLatestCommandMap) {
        opts.setRequestLatestCommandMap((prev) => {
          const next = { ...prev };
          const nextUpdated = typeof cmd.updatedAt === 'number' ? cmd.updatedAt : 0;

          for (const targetId of targetDeviceIds) {
            const existing = next[targetId];
            const existingUpdated = existing && typeof existing.updatedAt === 'number' ? existing.updatedAt : 0;
            if (existing && nextUpdated && nextUpdated < existingUpdated) continue;

            const nextStatus = displayStatus ?? existing?.status;
            const nextStatusInfo = typeof cmd.status_info === 'string' ? cmd.status_info : existing?.status_info;

            next[targetId] = {
              ...(existing || {}),
              ...(cmd as CommandDdbItem),
              deviceId: targetId,
              status: nextStatus,
              status_info: nextStatusInfo,
            } as CommandDdbItem;
          }

          return next;
        });
      }
    });

    return () => {
      unsubscribe?.();
    };
  }, []);

  /** On WebSocket reconnect, bust the status cache so the seed query re-runs immediately. */
  useEffect(() => {
    if (!window.fleetAPI?.onWsReconnected) return;
    const unsubscribe = window.fleetAPI.onWsReconnected(() => {
      setWsReconnectKey((k) => k + 1);
    });
    return () => unsubscribe?.();
  }, []);

  /** Tick "now" every 5s so online cutoff and relative times stay fresh. */
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(id);
  }, []);

  /** Periodic safety-net re-seed (status + commands) in case a stalled socket is missed. */
  useEffect(() => {
    const id = setInterval(() => setReseedKey((k) => k + 1), 90_000);
    return () => clearInterval(id);
  }, []);

  /** Subscribe to device status updates (websocket); batch into one React update per animation frame. */
  useEffect(() => {
    if (!window.fleetAPI?.onDeviceStatusUpdate) return;

    function applyStatusPatch(patch: Record<string, DeviceStatus>) {
      const ids = Object.keys(patch);
      if (ids.length === 0) return;
      const seenAt = Date.now();
      setStatusMap((prev) => ({ ...prev, ...patch }));
      setLastStatusSeen((prev) => {
        const next = { ...prev };
        for (let i = 0; i < ids.length; i += 1) {
          const deviceId = ids[i];
          const fromStatus = toStatusSeenMs(patch[deviceId]);
          next[deviceId] = Math.max(prev[deviceId] ?? 0, seenAt, fromStatus);
        }
        return next;
      });
    }

    function flushStatusBatch() {
      statusFlushTimerRef.current = null;
      const patch = statusPendingRef.current;
      statusPendingRef.current = {};
      applyStatusPatch(patch);
    }

    function scheduleStatusFlush() {
      if (statusFlushTimerRef.current != null) return;
      // setTimeout(0) batches same-burst updates like rAF but fires reliably when
      // the Electron window is hidden or minimised (rAF is throttled to ~1fps there).
      statusFlushTimerRef.current = setTimeout(flushStatusBatch, 0);
    }

    const unsubscribe = window.fleetAPI.onDeviceStatusUpdate((msg: any) => {
      const deviceId = typeof msg?.deviceId === 'string' ? msg.deviceId.trim() : '';
      const status = msg?.status as DeviceStatus | undefined;
      if (!deviceId || !status) return;

      const normalizedId = normalizeFleetStatusDeviceId(deviceId);
      statusPendingRef.current[normalizedId] = normalizeDeviceStatus({
        ...status,
        deviceId: normalizedId,
      });
      scheduleStatusFlush();
    });

    return () => {
      unsubscribe?.();
      if (statusFlushTimerRef.current != null) {
        clearTimeout(statusFlushTimerRef.current);
        statusFlushTimerRef.current = null;
      }
      const leftover = statusPendingRef.current;
      statusPendingRef.current = {};
      applyStatusPatch(leftover);
    };
  }, []);

  /** Seed status map via backend API for the current device scope. */
  useEffect(() => {
    if (!enableInitialStatusQuery) return;
    if (!seedDeviceIds.length) return;
    if (!window.eventsAPI) return;
    if (!isAdmin && !scopedEventId) return;

    const statusKey = scopedEventId ? `statuses:${scopedEventId}` : 'statuses';
    if (statusReloadKey || wsReconnectKey > 0 || reseedKey > 0) bustCache(statusKey);

    let cancelled = false;

    async function loadInitialStatus() {
      try {
        const res = await withCache(statusKey, LIVE_TTL, () =>
          scopedEventId && !isAdmin
            ? window.eventsAPI.queryEventDeviceStatuses(scopedEventId, seedDeviceIds)
            : window.eventsAPI.queryDeviceStatuses(seedDeviceIds),
        );
        if (cancelled) return;
        if (res?.ok === false) throw new Error(res?.error || 'Failed to load device statuses');

        const statusData = res?.data as { statuses?: unknown } | undefined;
        const rows2 = Array.isArray(statusData?.statuses) ? statusData.statuses : [];

        const next: Record<string, DeviceStatus> = {};
        const seenSeed: Record<string, number> = {};

        for (const row of rows2 as any[]) {
          const deviceId = typeof row?.deviceId === 'string' ? row.deviceId : '';
          if (!deviceId) continue;
          const normalizedId = normalizeFleetStatusDeviceId(deviceId);
          const status = normalizeDeviceStatus({
            ...(row as DeviceStatus),
            deviceId: normalizedId,
          });
          next[normalizedId] = status;
          const seenMs = toStatusSeenMs(status);
          if (seenMs > 0) seenSeed[normalizedId] = seenMs;
        }

        setStatusMap((prev) => ({ ...prev, ...next }));
        // Use Math.max so the fresher of the live-WS timestamp and the DB timestamp wins.
        // This is critical on reconnect: the DB may have heartbeats we missed while the WS was down.
        setLastStatusSeen((prev) => {
          const merged = { ...prev };
          for (const [id, seenMs] of Object.entries(seenSeed)) {
            merged[id] = Math.max(merged[id] ?? 0, seenMs);
          }
          return merged;
        });
      } catch (err) {
        logConsoleError('[useDeviceListLiveData] loadInitialStatus failed', err);
      } finally {
        if (!cancelled) setStatusInitiallyLoaded(true);
      }
    }

    void loadInitialStatus();

    return () => {
      cancelled = true;
    };
  }, [enableInitialStatusQuery, isAdmin, scopedEventId, seedDeviceIds, statusReloadKey, wsReconnectKey, reseedKey]);

  /** Seed latest command map via backend API for the current device scope. */
  useEffect(() => {
    if (!enableInitialCommandQuery) return;
    if (!seedDeviceIds.length) return;
    if (!window.eventsAPI) return;
    if (!isAdmin && !scopedEventId) return;

    const cmdKey = scopedEventId ? `commands:${scopedEventId}` : 'commands';
    if (statusReloadKey || wsReconnectKey > 0 || reseedKey > 0) bustCache(cmdKey);

    let cancelled = false;

    async function loadInitialCommands() {
      try {
        const res = await withCache(cmdKey, LIVE_TTL, () =>
          scopedEventId && !isAdmin
            ? window.eventsAPI.queryEventDeviceLatestCommands(scopedEventId, seedDeviceIds)
            : window.eventsAPI.queryDeviceLatestCommands(seedDeviceIds),
        );
        if (cancelled) return;
        if (res?.ok === false) throw new Error(res?.error || 'Failed to load device latest commands');

        const cmdData = res?.data as { commands?: unknown } | undefined;
        const rows2 = Array.isArray(cmdData?.commands) ? cmdData.commands : [];

        const byDevice: Record<string, CommandDdbItem> = {};
        const statusSeed: Record<string, string | undefined> = {};

        for (const raw of rows2 as any[]) {
          const ddb = raw as CommandDdbItem;
          const targetDeviceIds = deriveCommandTargetDeviceIds(ddb);
          if (!targetDeviceIds.length) continue;

          const updated = typeof ddb.updatedAt === 'number' ? ddb.updatedAt : 0;
          for (const targetId of targetDeviceIds) {
            const existing = byDevice[targetId];
            const existingUpdated = existing && typeof existing.updatedAt === 'number' ? existing.updatedAt : 0;
            if (!existing || updated > existingUpdated) {
              byDevice[targetId] = { ...ddb, deviceId: targetId };
            }
          }
        }

        for (const [deviceId, cmd] of Object.entries(byDevice)) {
          const status = typeof cmd.status === 'string' ? cmd.status.toUpperCase() : undefined;
          statusSeed[deviceId] = status;
        }

        setLatestCommandMap((prev) => ({ ...prev, ...byDevice }));
        setCommandStatusMap((prev) => ({ ...prev, ...statusSeed }));
      } catch (err) {
        logConsoleError('[useDeviceListLiveData] loadInitialCommands failed', err);
      } finally {
        if (!cancelled) setCommandInitiallyLoaded(true);
      }
    }

    void loadInitialCommands();

    return () => {
      cancelled = true;
    };
  }, [enableInitialCommandQuery, isAdmin, scopedEventId, seedDeviceIds, statusReloadKey, wsReconnectKey, reseedKey]);

  const willQueryLiveData = (isAdmin || !!scopedEventId) && seedDeviceIds.length > 0;
  const statusLoading =
    willQueryLiveData && enableInitialStatusQuery && !statusInitiallyLoaded;
  const commandLoading =
    willQueryLiveData && enableInitialCommandQuery && !commandInitiallyLoaded;
  const liveDataLoading = statusLoading || commandLoading;

  return {
    statusMap,
    lastStatusSeen,
    now,
    latestCommandMap,
    commandStatusMap,
    liveDataLoading,
    statusLoading,
    commandLoading,
  };
}
